import { Avatar, Button, Grid, Stack, Typography } from '@mui/material';
import { ButtonLinkOrDownload, MAX_CHARS_BTN_LIMIT } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { Notification_Type } from 'Enum/Home';
import { useDictionary, useIsDesktop } from 'Hooks';
import { NewIcon, NewLabelIcon } from 'Images';
import { MedicalReferralListMobile } from './MedicalReferralListMobile';
import {
  IUserUpdates,
  NotifSubjectsType,
  setNotifReaded,
} from 'API/Home/userUpdates';
import { shortDateFormat } from 'Config';
import { format } from 'date-fns';
import { useLoader } from 'Hooks/useLoader';
import { getFile } from 'API';
import { downloadFile } from 'Utils';
import { useEffect, useState } from 'react';
import { requestFile } from 'API/medicalReferrals';
import { useNavigate } from 'react-router-dom';
import { sendEvent } from 'Utils/sendEvent';

interface IRowProps {
  data: IUserUpdates;
  setShowDialog?: () => void;
}

export const Row = ({ data, setShowDialog }: IRowProps) => {
  const { isLoading, loader, setIsLoading } = useLoader('scale(0.5)');
  const [isRead, setIsRead] = useState<Boolean>(data.readedDate ? true : false);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Home).userUpdates;
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setIsLoading(true);
    sendEvent('Home page', 'UpdatesCta', data.title, 'Download');
    setIsRead(true);
    await setNotifReaded(data.id);
    e.stopPropagation();
    let file;
    if (data.notifEntityId === NotifSubjectsType.MedicalReferrals) {
      file = await requestFile(jsonData.Items[0].Id);
    } else if (data.notifEntityId === NotifSubjectsType.Documents) {
      file = await getFile(jsonData.Items[0].Id);
    }
    setIsLoading(false);
    if (file && file.fileContentBase64) {
      downloadFile(
        file.fileContentBase64,
        'pdf',
        file.documentName || 'New Document'
      );
    }
  };

  const handleNavigate = async () => {
    sendEvent('Home page', 'UpdatesCta', data.title, `Navigate to ${url}`);
    navigate(url);
    setIsRead(true);
    await setNotifReaded(data.id);
  };

  useEffect(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  const jsonData = data.entityJSON && JSON.parse(data.entityJSON);

  let label = '';
  let url = '';

  switch (data.notifEntityId) {
    case NotifSubjectsType.MedicalReferrals:
      label = dictionary.allMedicalReferrals;
      url = '/medical-referrals';
      break;
    case NotifSubjectsType.Documents:
      label = dictionary.allDocuments;
      url = '/documents';
      break;
    case NotifSubjectsType.PaymentsAndDeductions:
      label = dictionary.allPaymentAndDeductions;
      url = '/payments&Deductions';
      break;

    case Notification_Type.payslips:
      label = dictionary.allPayslips;
      url = '/payslips';
      break;
    case Notification_Type.form106:
      label = dictionary.allForms106;
      url = '/form106';
      break;
    case Notification_Type.medicationOrders:
      label = dictionary.allMedicationOrders;
      url = '/medicationOrders';
      break;
    case Notification_Type.prescriptions:
      label = dictionary.allPrescriptions;
      url = '/prescriptions';
      break;
    case Notification_Type.requests:
      label = dictionary.allRequests;
      url = '/requests';
      break;
    case Notification_Type.equipments:
      label = dictionary.allEquipments;
      url = '/equipments';
      break;
    case Notification_Type.recognitionProcess:
      label = dictionary.recognitionStatus;
      url = '/recognitionProcess';
      break;
    default:
      break;
  }

  const shouldPopup =
    jsonData &&
    jsonData?.Title !== null &&
    jsonData.Items.length > 1 &&
    data.notifEntityId === NotifSubjectsType.MedicalReferrals;

  return (
    <Grid
      container
      sx={{
        borderBottom: '1px solid',
        borderColor: 'primary.shade_100',
        p: isDesktop ? '24px 8px' : '24px 12px',
      }}
      tabIndex={0}
    >
      <Grid item xs={12} md={5}>
        <Stack flexDirection={'row'}>
          <Typography variant='body1'>{data.title}</Typography>
          {!isRead && (
            <Avatar
              src={isDesktop ? NewIcon : NewLabelIcon}
              alt=''
              sx={
                isDesktop
                  ? { width: '44px', height: '20px', m: '0 8px' }
                  : {
                      width: '42px',
                      height: '26px',
                      position: 'absolute',
                      right: '-8px',
                    }
              }
            />
          )}
        </Stack>
        <Stack width='fit-content'>
          <Typography variant='body6'>{data.description}</Typography>
          {isDesktop && shouldPopup && (
            <Button
              sx={{
                width: 'fit-content',
                fontSize: '16px',
                color: 'primary.main',
                fontWeight: 600,
                p: 0,
              }}
              onClick={() => setShowDialog && setShowDialog()}
            >
              {dictionary.toDownloadMedicalReferrals}
            </Button>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12} md={2}>
        <Stack
          sx={
            isDesktop
              ? { m: '0 32px' }
              : { flexDirection: 'row', m: '12px 0', alignItems: 'center' }
          }
        >
          <Typography
            variant='body3'
            sx={{ mr: isDesktop ? undefined : '6px' }}
          >
            {Dictionary[DICTIONARY.Home].userUpdates.date}
          </Typography>
          <Typography variant='body5'>
            {data.createDate &&
              format(new Date(data.createDate), shortDateFormat)}
          </Typography>
        </Stack>
        {shouldPopup && <MedicalReferralListMobile items={jsonData?.Items} />}
      </Grid>
      <Grid item xs={12} md={5}>
        {isLoading ? (
          <Stack height={'20px'} justifyContent={'center'}>
            {loader}
          </Stack>
        ) : (
          <ButtonLinkOrDownload
            label={label}
            url={url}
            downloadFile={
              jsonData?.Title === null && jsonData?.Items[0].Id && download
            }
            isDownload={jsonData?.Title === null && jsonData?.Items[0].Id}
            isCallbackNavigation
            callbackNavigation={handleNavigate}
            isWidthBtn={label.length > MAX_CHARS_BTN_LIMIT}
          />
        )}
      </Grid>
    </Grid>
  );
};
